import useDateHelperUtils from './dateHelperUtils.ts';
import useUtils from './utils.ts';
import {
  IRoom,
  RoomTypeAvailability,
  IVatSettings,
  Rate,
} from 'src/types/common';
import {
  RateDTO,
  BookingEngineArticleDTO,
} from 'src/services/api/data-contracts';
import { IReservationItemService } from 'src/types/common.ts';

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

const dateUtils = useDateHelperUtils();
const utils = useUtils();

function calculatePerNightRate(rate: number, from: string, to: string) {
  return rate / dateUtils.calculateNumberOfNights(from, to);
}

function calculateLowestRoomRate(
  room: IRoom,
  rates: RateDTO[],
  useOriginalValues: boolean,
): number {
  let total = 0;
  const roomRates = [];

  rates.forEach((rateDTO: RateDTO) => {
    let nightlyRate = 0;
    const rate = new Rate(rateDTO);

    if (useOriginalValues) {
      nightlyRate = calculateRoomRate(
        room.NumberOfAdults,
        room.NumberOfChildren,
        room.NumberOfInfants,
        rate.OriginalBaseRate,
        rate.BaseNumberOfAdults,
        rate.OriginalCostPerRoom,
        rate.OriginalCostPerAdult,
        rate.OriginalCostPerChild,
        rate.OriginalCostPerInfant,
        rate.OriginalPerPersonCostPerAdult,
        rate.OriginalPerPersonCostPerChild,
        rate.OriginalPerPersonCostPerInfant,
      );
    } else {
      nightlyRate = calculateRoomRate(
        room.NumberOfAdults,
        room.NumberOfChildren,
        room.NumberOfInfants,
        rate.BaseRate,
        rate.BaseNumberOfAdults,
        rate.CostPerRoom,
        rate.CostPerAdult,
        rate.CostPerChild,
        rate.CostPerInfant,
        rate.PerPersonCostPerAdult,
        rate.PerPersonCostPerChild,
        rate.PerPersonCostPerInfant,
      );
    }
    roomRates.push(nightlyRate);
  });

  const minimumRate = Math.min(...roomRates);

  total = total + minimumRate;

  return total;
}

function calculateTotalRoomRate(
  rooms: Array<IRoom>,
  roomTypes: Array<RoomTypeAvailability>,
  useOriginalValues: boolean,
): number {
  let total = 0;

  rooms.forEach((v: IRoom) => {
    const roomRates = [];

    roomTypes.forEach((rt: RoomTypeAvailability) => {
      const isRoomTypeOccupancyValid = utils.checkIfOccupancyIsValid(
        v.NumberOfAdults,
        v.NumberOfChildren,
        v.NumberOfInfants,
        rt.RoomType.MaximumOccupancy,
        rt.RoomType.MinimumOccupancy,
        rt.RoomType.AdultMaximumOccupancy,
        rt.RoomType.ChildMaximumOccupancy,
      );
      if (isRoomTypeOccupancyValid) {
        rt.Rates.forEach((rateDTO: RateDTO) => {
          const rate = new Rate(rateDTO);

          let ratePlanOccupancyIsValid = true;
          if (rate.UseOccupancyForRatePlan) {
            ratePlanOccupancyIsValid = utils.checkIfOccupancyIsValid(
              v.NumberOfAdults,
              v.NumberOfChildren,
              v.NumberOfInfants,
              utils.getSmallestMaximumValue(
                rate.MaximumOccupancy,
                rt.RoomType.MaximumOccupancy,
              ),
              utils.getLargestMinimumValue(
                rate.MinimumOccupancy,
                rt.RoomType.MinimumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                rate.AdultMaximumOccupancy,
                rt.RoomType.AdultMaximumOccupancy,
              ),
              utils.getSmallestMaximumValue(
                rate.ChildMaximumOccupancy,
                rt.RoomType.ChildMaximumOccupancy,
              ),
            );
          }
          if (ratePlanOccupancyIsValid) {
            let nightlyRate = 0;
            if (useOriginalValues) {
              nightlyRate = calculateRoomRate(
                v.NumberOfAdults,
                v.NumberOfChildren,
                v.NumberOfInfants,
                rate.OriginalBaseRate,
                rate.BaseNumberOfAdults,
                rate.OriginalCostPerRoom,
                rate.OriginalCostPerAdult,
                rate.OriginalCostPerChild,
                rate.OriginalCostPerInfant,
                rate.OriginalPerPersonCostPerAdult,
                rate.OriginalPerPersonCostPerChild,
                rate.OriginalPerPersonCostPerInfant,
              );
            } else {
              nightlyRate = calculateRoomRate(
                v.NumberOfAdults,
                v.NumberOfChildren,
                v.NumberOfInfants,
                rate.BaseRate,
                rate.BaseNumberOfAdults,
                rate.CostPerRoom,
                rate.CostPerAdult,
                rate.CostPerChild,
                rate.CostPerInfant,
                rate.PerPersonCostPerAdult,
                rate.PerPersonCostPerChild,
                rate.PerPersonCostPerInfant,
              );
            }
            roomRates.push(nightlyRate);
          }
        });
      }
    });

    const minimumRate = Math.min(...roomRates);

    total = total + minimumRate;
  });

  return total;
}

/*
This method is taken from the backend - see CalculateReservationItemRoomRate from RateHelper.cs
*/
function calculateRoomRate(
  NumberOfAdults: number,
  NumberOfChildren: number,
  NumberOfInfants: number,
  baseRate: number,
  baseNumberOfAdults: number | null,
  amountPerRoom: number,
  additionalAdultRate: number,
  additionalChildRate: number,
  additionalInfantRate: number,
  perPersonAdultRate: number = 0,
  perPersonChildRate: number = 0,
  perPersonInfantRate: number = 0,
): number {
  let reservationItemRoomRate = 0;

  const defaultNumberOfAdults = baseNumberOfAdults ?? 1;

  const additionalAdults =
    NumberOfAdults > defaultNumberOfAdults
      ? NumberOfAdults - defaultNumberOfAdults
      : 0;
  const childMultiplier =
    NumberOfAdults == 0 && NumberOfChildren > 0
      ? NumberOfChildren - 1
      : NumberOfChildren;

  reservationItemRoomRate += baseRate + amountPerRoom;

  reservationItemRoomRate +=
    additionalAdultRate * additionalAdults + // Yes this is correct, only the Base number of adults are "free"
    additionalChildRate * childMultiplier +
    additionalInfantRate * NumberOfInfants;

  reservationItemRoomRate +=
    perPersonAdultRate * NumberOfAdults + // Yes this is correct, all adults are counted for the per person rate.
    perPersonChildRate * NumberOfChildren +
    perPersonInfantRate * NumberOfInfants;

  const roundedReservationItemRoomRate = roundToTwoDecimals(
    reservationItemRoomRate,
  );

  return roundedReservationItemRoomRate;
}

function calculateServiceCost(
  resItemService: IReservationItemService,
  totalDays: number,
  totalGuests: number,
): number {
  let cost = 0;

  const guestFactor = resItemService.Service.ChargePerPerson ? totalGuests : 1;

  let serviceQuantity = 1;
  const ct = resItemService.Service.ChargeType;
  switch (ct) {
    case 'PerStay':
      serviceQuantity = 1;
      break;
    case 'PerDay':
      serviceQuantity = totalDays;
      break;
    case 'PerQuantity':
      serviceQuantity = resItemService.Quantity;
      break;
    default:
      break;
  }

  resItemService.Service.Articles.forEach(
    (article: BookingEngineArticleDTO) => {
      cost = cost + article.Price * serviceQuantity * guestFactor;
    },
  );

  return cost;
}

function calcuateTotalServiceCost(
  services: Array<IReservationItemService>,
  totalDays: number,
  totalGuests: number,
): number {
  let cost = 0;
  services.forEach((resItemService: IReservationItemService) => {
    resItemService.TotalCost = calculateServiceCost(
      resItemService,
      totalDays,
      totalGuests,
    );
    cost += resItemService.TotalCost;
  });
  return cost;
}

function calculateVatAmount(amount: number, settings: IVatSettings): number {
  const vat =
    (amount /
      (settings.isStandardTaxExclusive ? 100 : 100 + settings.vatPercentage)) *
    settings.vatPercentage;
  return vat;
}

function calculatePolicyPaymentAmount(
  ratePlan: RateDTO,
  totalCost: number,
  dateOfArrival: string,
  numberOfAdults: number,
) {
  let calculatedCost = 0;

  for (
    let index = 0;
    index < ratePlan.Policy.PaymentPolicyConfiguration.length;
    index++
  ) {
    let configCost = 0;
    let chargeConfig = false;
    const configElement = ratePlan.Policy.PaymentPolicyConfiguration[index];
    if (configElement.AutomaticPaymentType == 'TimeOfBooking') {
      chargeConfig = true;
    } else if (configElement.AutomaticPaymentType == 'DaysBeforeArrival') {
      const paymentDate = dateUtils.calculateDate(
        dateOfArrival,
        -1 * configElement.DaysBeforeArrival,
      );

      const inPaymentWindow = dateUtils.isSameOrAfterDate(
        dateUtils.today(),
        paymentDate,
      );
      if (inPaymentWindow) {
        chargeConfig = true;
      }
    }

    if (chargeConfig) {
      if (configElement.ChargeUnit == 'Percentage') {
        configCost = roundToTwoDecimals(
          totalCost * (configElement.ChargeValue / 100),
        );
      } else if (configElement.ChargeUnit == 'Value') {
        if (configElement.ChargePerAdultGuest) {
          configCost = configElement.ChargeValue * numberOfAdults;
        } else {
          configCost = configElement.ChargeValue;
        }
      }

      if (configCost > totalCost) {
        configCost = totalCost;
      }
    }

    calculatedCost += configCost;
  }

  if (calculatedCost > totalCost) {
    calculatedCost = totalCost;
  }

  return calculatedCost;
}

export default function useRateHelperUtils() {
  return {
    calculateVatAmount: calculateVatAmount,
    calculateRoomRate: calculateRoomRate,
    calculateServiceCost: calculateServiceCost,
    calcuateTotalServiceCost: calcuateTotalServiceCost,
    calculateTotalRoomRate: calculateTotalRoomRate,
    calculatePerNightRate: calculatePerNightRate,
    calculateLowestRoomRate: calculateLowestRoomRate,
    calculatePolicyPaymentAmount: calculatePolicyPaymentAmount,
  };
}
